import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight, Lightning, Lock, ShieldCheck } from "@phosphor-icons/react";
import ReserveModal from "@/components/site/ReserveModal";
import { PRICES } from "@/lib/visibility";

const includes = [
  "Mobile-optimized website — up to five standard pages, or a one-page site when that suits the business",
  "Domain setup, hosting & SSL — handled",
  "Click-to-call & tap-to-text buttons",
  "Quote & contact forms",
  "Instant lead alerts to your email",
  "Private client portal for leads, updates, and business info",
  "Self-service business updates through the Command Center — update info, photos, hours, services, and more on your own, any time",
  "Security patches, backups & uptime monitoring",
];

const SCOPE = [
  [
    "Scope",
    "Up to five standard pages, with the exact page list agreed before work begins. Additional pages, major changes to the approved direction, advanced features, and custom integrations are quoted separately before we proceed.",
  ],
  [
    "Revisions",
    "Two consolidated revision rounds before launch. Fixing errors in our own work never counts toward those rounds. First drafts are usually ready within a few business days of receiving your content.",
  ],
  [
    "If you cancel",
    "Cancellation takes effect at the end of your billing cycle. The hosted site is deactivated and a backup is kept for 30 days. Your text and image content can be exported on request, and your domain can be transferred once the account is settled.",
  ],
];

const OTHER = [
  { id: "gbp", name: "Google Business Profile setup or cleanup", note: "One-time · per location · no subscription", price: `$${PRICES.gbpSetup}`, href: "/google-business-profile" },
  { id: "visibility", name: "Local Visibility", note: `Four content pieces a month on Google + Facebook or Instagram. Both socials: $${PRICES.monthly + PRICES.addon}/mo.`, price: `$${PRICES.onboarding} onboarding + $${PRICES.monthly}/mo`, href: "/local-visibility" },
];

export default function Pricing() {
  const [reserveOpen, setReserveOpen] = useState(false);

  return (
    <section
      id="pricing"
      data-tone="light"
      data-testid="pricing-section"
      className="tone-light relative pt-16 pb-16 sm:pt-28 sm:pb-24 md:pt-36 md:pb-32 bg-white scroll-mt-16 md:scroll-mt-20"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
        {/* Header */}
        <div className="max-w-2xl">
          <div className="text-[11.5px] tracking-[0.22em] uppercase text-brand-700 flex items-center gap-3">
            <span className="block w-8 h-px bg-brand-700" />
            Pricing
            <span className="normal-case tracking-normal text-zinc-600">· Website</span>
          </div>
          <h2 className="mt-5 font-display font-bold text-[34px] sm:text-5xl lg:text-[56px] leading-[1.02] tracking-tight text-zinc-900">
            Website Build &amp; Management.
          </h2>
        </div>

        {/* Card — promise only */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          data-testid="pricing-card-starter"
          className="relative mt-9 sm:mt-16 max-w-3xl mx-auto rounded-3xl border border-zinc-900/10 bg-gradient-to-b from-[#121214] to-[#0a0a0c] overflow-hidden shadow-[0_40px_90px_-30px_rgba(24,24,27,0.5)]"
        >
          <div className="absolute -top-32 -right-24 w-80 h-80 rounded-full bg-brand-500/[0.18] blur-3xl pointer-events-none" />

          <div className="relative p-6 sm:p-10 md:p-12">
            {/* Price */}
            <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1">
              <span className="inline-flex items-baseline gap-x-3 whitespace-nowrap">
                <span className="font-display text-5xl sm:text-7xl tracking-tighter text-white leading-none">
                  $499
                </span>
                <span className="text-zinc-400 text-base sm:text-lg">build</span>
              </span>
              <span className="hidden sm:inline text-zinc-700 text-4xl mx-1">+</span>
              <span className="inline-flex items-baseline gap-x-3 whitespace-nowrap">
                <span className="sm:hidden text-zinc-600 text-2xl -mr-1.5">+</span>
                <span className="font-display text-3xl sm:text-5xl tracking-tighter text-white leading-none">
                  $79
                </span>
                <span className="text-zinc-400 text-[15px] sm:text-base">/month</span>
              </span>
            </div>

            {/* Subtitle */}
            <p
              data-testid="pricing-subtitle"
              className="mt-6 text-zinc-300 text-[14.5px] sm:text-[15px] leading-relaxed max-w-xl"
            >
              $99 deposit reserves your slot and is credited toward the $499 build.
              Remaining balance due before launch. Monthly management begins once
              your site goes live. Month-to-month. Cancel anytime.
            </p>

            {/* CTAs */}
            <div className="mt-7 sm:mt-10 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
              <button
                type="button"
                onClick={() => setReserveOpen(true)}
                data-testid="pricing-cta-reserve"
                className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors duration-200"
              >
                <Lightning size={15} weight="fill" className="text-brand-600" />
                Reserve slot · $99 deposit
                <ArrowUpRight
                  size={16}
                  weight="bold"
                  className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </button>
              <a
                href="#contact"
                data-testid="pricing-cta-primary"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-white/15 bg-white/[0.03] text-white hover:bg-brand-700/25 hover:border-brand-500/50 transition-colors duration-200 text-[14px]"
              >
                Talk to us first
              </a>
            </div>

            {/* Trust strip */}
            <div
              data-testid="pricing-trust-strip"
              className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12.5px] text-zinc-400"
            >
              <span className="inline-flex items-center gap-1.5">
                <Lock size={12} weight="regular" className="text-zinc-300" />
                Powered by Stripe
              </span>
              <span className="text-zinc-700">·</span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck size={13} weight="regular" className="text-emerald-400/80" />
                Cancel anytime
              </span>
            </div>
          </div>
        </motion.div>

        {/* What's included — separate, quieter section below */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          data-testid="pricing-includes"
          className="mt-9 sm:mt-20 max-w-3xl mx-auto"
        >
          <div className="flex items-center gap-3 text-[11.5px] tracking-[0.22em] uppercase text-brand-700">
            <span className="block w-8 h-px bg-brand-700" />
            Included
          </div>
          <p
            data-testid="pricing-trust-line"
            className="mt-5 text-[14px] sm:text-[14.5px] text-zinc-700 leading-relaxed max-w-2xl"
          >
            WebJesters does not hand you a website and disappear. We build it,
            host it, maintain it, and support it long-term.
          </p>
          <ul className="mt-6 grid sm:grid-cols-2 gap-x-10 gap-y-3">
            {includes.map((line) => (
              <li key={line} className="flex items-start gap-3">
                <Check
                  size={14}
                  weight="bold"
                  className="mt-1 flex-shrink-0 text-brand-700"
                />
                <span className="text-[14px] sm:text-[14.5px] text-zinc-700 leading-relaxed">
                  {line}
                </span>
              </li>
            ))}
          </ul>
          <p
            data-testid="pricing-included-note"
            className="mt-8 text-[13px] text-zinc-600 leading-relaxed max-w-2xl"
          >
            Quote &amp; call automations are optional, quoted individually after a
            compatibility check, and not part of the monthly fee.
          </p>
        </motion.div>

        {/* Build scope, revisions, cancellation */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          data-testid="pricing-scope"
          className="mt-9 sm:mt-16 max-w-3xl mx-auto rounded-3xl bg-[#f6f5f1] border border-zinc-200 p-5 sm:p-8 grid sm:grid-cols-3 gap-5 sm:gap-8"
        >
          {SCOPE.map(([h, p]) => (
            <div key={h}>
              <h3 className="text-[11px] tracking-[0.22em] uppercase text-brand-700">{h}</h3>
              <p className="mt-2.5 text-[13.5px] text-zinc-700 leading-relaxed">{p}</p>
            </div>
          ))}
        </motion.div>

        {/* Other services — compact strip */}
        <div data-testid="pricing-other-services" className="mt-9 sm:mt-16 max-w-3xl mx-auto">
          <div className="flex items-center gap-3 text-[11.5px] tracking-[0.22em] uppercase text-brand-700">
            <span className="block w-8 h-px bg-brand-700" />
            <span>Google services <span className="hidden sm:inline text-zinc-600">· priced separately</span></span>
          </div>
          <div className="mt-5 card-light rounded-2xl divide-y divide-zinc-200 overflow-hidden">
            {OTHER.map((o) => (
              <Link
                key={o.href}
                to={o.href}
                data-testid={`pricing-other-${o.id}`}
                className="group flex items-center justify-between gap-3 px-4 py-4 sm:p-5 sm:px-6 hover:bg-brand-50/60 transition-colors"
              >
                <div className="min-w-0">
                  <div className="text-[14.5px] text-zinc-900">{o.name}</div>
                  <div className="mt-0.5 text-[12.5px] text-zinc-600">{o.note}</div>
                </div>
                <div className="flex items-center gap-3 whitespace-nowrap">
                  <span className="text-[14px] text-zinc-900">{o.price}</span>
                  <ArrowUpRight size={13} weight="bold" className="text-zinc-400 group-hover:text-brand-700 transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
      <ReserveModal open={reserveOpen} onClose={() => setReserveOpen(false)} />
    </section>
  );
}
